import { createGlobalStyle } from "styled-components";

export default createGlobalStyle`

:root {
  --font-urw: "urw-din", sans-serif;
  --focus-visible: 0.2rem solid #5b9dd9;

  /**
   * Colours
   */

  --colour-black: #000000;
  --colour-grey-dark: #333333;
  --colour-grey-medium: #666666;
  --colour-grey-inactive: #b2b2b2;
  --colour-grey-pinline: #cccccc;
  --colour-grey-background: #e9e9e9;
  --colour-white: #ffffff;
  --colour-copy: var(--colour-black);
  --black: var(--colour-black);
  --dark-grey: var(--colour-grey-dark);
  --medium-grey: var(--colour-grey-medium);
  --inactive-state-grey: var(--colour-grey-inactive);
  --pinline-grey: var(--colour-grey-pinline);
  --background-grey: var(--colour-grey-background);
  --white: var(--colour-white);


  /*
   * Feedback Colours
   */
  --colour-red-3: #990000;
  --colour-red-2: #c86464;
  --colour-red-1: #fbebeb;
  --warning-red: var(--colour-red-3);
  --medium-red: var(--colour-red-2);
  --background-red: var(--colour-red-1);

  --colour-yellow-3: #785e1c;
  --colour-yellow-2: #ab934f;
  --colour-yellow-1: #f8f2d5;
  --caution-yellow: var(--colour-yellow-3);
  --medium-yellow: var(--colour-yellow-2);
  --background-yellow: var(--colour-yellow-1);

  --colour-blue-3: #31668f;
  --colour-blue-2: #5b9dd9;
  --colour-blue-1: #e3edf2;
  --info-blue: var(--colour-blue-3);
  --focus-blue: var(--colour-blue-2);
  --background-blue: var(--colour-blue-1);

  --colour-green-3: #227733;
  --colour-green-2: #5aab6a;
  --colour-green-1: #dff0d8;
  --success-green: var(--colour-green-3);
  --medium-green: var(--colour-green-2);
  --background-green: var(--colour-green-1);

  /**
   * Spacing Units
   * These tie in directly with the coloured spaces depicted in design mocks
   * Responsiveness is built-in to the variable, so no component-level tweaks needed
   */
  --space-red: 6rem;
  --space-pink: 4rem;
  --space-purple: 2rem;
  --space-green: 2rem;
  --space-blue: 1rem;
  --space-orange: .5rem;

  @media(min-width: 1024px) {
    --space-purple: 3rem;
    --space-pink: 6rem;
    --space-red: 10rem;
  }

  /**
   * Spacing Units - Aliases
   * Let's be honest, the colour names/scale is hard to remember.
   */
  --space-xs: var(--space-orange);
  --space-s: var(--space-blue);
  --space-m: var(--space-green);
  --space-l: var(--space-purple);
  --space-xl: var(--space-pink);
  --space-xxl: var(--space-red);

  /**
   * CatIntro Banners
   */
   --banner-height-xs: 10rem; /* 150px */
   --banner-height-sm: 12rem; /* 180px */
   --banner-height-md: 13.333rem; /* 200px */
   --banner-height-lg: 16.666rem; /* 250px */
   --banner-height-xl: 23.333rem; /* 350px */

   /**
    * Common breakpoints
    */
   --breakpoint-sm: 576px;
   --breakpoint-md: 768px;
   --breakpoint-lg: 992px;
   --breakpoint-xl: 1200px;
   /**
    * ProductTiles
    */
   --product-tile-2-col: 50%;
   --product-tile-3-col: 33.33%;
   --product-tile-4-col: 25%;
}

html {
  height: 100%;
  height: -webkit-fill-available;
  font-size: 93.75%; /* 15px */
  scroll-behavior: smooth;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
}

ul[class],
ol[class] {
  padding: 0;
}

body,
ul[class],
ol[class],
li,
figure,
figcaption,
blockquote,
dl,
dd {
  margin: 0;
}
body {
  min-height: 100vh;
  min-height: -webkit-fill-available;
  text-rendering: optimizeSpeed;
  line-height: 1.5rem;
  color: var(--colour-copy);
  font-family: var(--font-urw);
  font-weight: normal;
  font-size: 1rem;
}

ol, ul {
  list-style-position: outside;
  margin: 1rem 0 0 0;
  padding: 0 0 0 2rem;
  max-width: 80ch;
}
ul {
  list-style-type: disc;
}
li {
  line-height: 1.5rem;
  padding-bottom: 0.5rem;
}

ul[class],
ol[class] {
  list-style: none;
}

/* helper class to hide scrollbars */
.hide--scrollbar {
  /* Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;  /* Firefox */
}

a:not([class]) {
  text-decoration: underline;
  text-decoration-skip-ink: auto;
}

a:focus, button:focus {
  outline: none;
}

a:focus-visible, button:focus-visible {
  outline: var(--focus-visible);
}

a:-moz-focus-visible, button:-moz-focus-visible,
:-moz-focusring {
  outline: var(--focus-visible);
}

/**
  *  Heading tags; h1, h2, h3, h4
  *  Ideally all Heading tags will use the <H1>, <H2>, <H3>, and <H4> provided by @arcteryx/components-typography
  *  but this will help catch any tags that might not be styled-components
  */
h1, h2, h3, h4 {
  font-family: urw-din-condensed, arial, sans-serif;
  font-weight: 700;
  line-height: 1.5rem;
  margin-top: 0;
  text-transform: uppercase;
}
h1 {
  margin-bottom: 1rem;
  font-size: 1.5rem;
}
h2 {
  margin-bottom: 1rem;
  font-size: 1.25rem;
}
h3 {
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}
h4 {
  margin-bottom: 0.5rem;
  font-size: 1rem;
}
/**
  * <p>
  * Ideally all P tags will use the <P> provided by @arcteryx/components-typography
  * but this is a catches any p tags that might not be styled-components
  */
p {
  margin: 0 0 1.5rem 0;
  font-family: "urw-din", arial, sans-serif;
  font-size: 1rem;
  font-weight: 400;
  font-style: normal;
  line-height: 1.5rem;
  max-width: 80ch;
  color: var(--colour-copy);

  strong, b {
    font-weight: 600;
  }

  em, i {
    font-weight: 400;
    font-style: italic;
  }

  &:last-of-type {
    margin-bottom: 0;
  }

}

strong, b {
  font-weight: 600;
}

em, i {
  font-weight: 400;
  font-style: italic;
}

img {
  max-width: 100%;
  display: block;
}

.lazyload,
.lazyloading {
  opacity: 0;
}

.lazyloaded {
  opacity: 1;
  transition: opacity 200ms;
}

/* Legacy class for header nav hover overlay */

.overlay-dark--full {
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 1;
  z-index: 250;
}

/**
 * a11y
 * For folks who's browser requests reduced motion
 */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

/*
* The Typekit service used to deliver this font or fonts for use on websites
* is provided by Adobe and is subject to these Terms of Use
* http://www.adobe.com/products/eulas/tou_typekit. For font license
* information, see the list below.
*
* urw-din:
*   - http://typekit.com/eulas/00000000000000003b9b0ee3
*   - http://typekit.com/eulas/00000000000000003b9b0ee4
*   - http://typekit.com/eulas/00000000000000003b9b0ee5
*   - http://typekit.com/eulas/00000000000000003b9b0ee6
*   - http://typekit.com/eulas/00000000000000003b9b0ee7
*   - http://typekit.com/eulas/00000000000000003b9b0ee8
* urw-din-condensed:
*   - http://typekit.com/eulas/00000000000000003b9b0ef9
*   - http://typekit.com/eulas/00000000000000003b9b0efa
*
* Â© 2009-2020 Adobe Systems Incorporated. All Rights Reserved.
*/
/*{"last_published":"2020-06-23 18:00:11 UTC"}*/
@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/570b14/00000000000000003b9b0ee3/27/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("woff2"),url("https://use.typekit.net/af/570b14/00000000000000003b9b0ee3/27/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("woff"),url("https://use.typekit.net/af/570b14/00000000000000003b9b0ee3/27/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i4&v=3") format("opentype");
font-display:swap;font-style:italic;font-weight:400;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/3bd84d/00000000000000003b9b0ee4/27/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("woff2"),url("https://use.typekit.net/af/3bd84d/00000000000000003b9b0ee4/27/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("woff"),url("https://use.typekit.net/af/3bd84d/00000000000000003b9b0ee4/27/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n4&v=3") format("opentype");
font-display:swap;font-style:normal;font-weight:400;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/601cb7/00000000000000003b9b0ee5/27/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i5&v=3") format("woff2"),url("https://use.typekit.net/af/601cb7/00000000000000003b9b0ee5/27/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i5&v=3") format("woff"),url("https://use.typekit.net/af/601cb7/00000000000000003b9b0ee5/27/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i5&v=3") format("opentype");
font-display:swap;font-style:italic;font-weight:500;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/e69b71/00000000000000003b9b0ee6/27/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3") format("woff2"),url("https://use.typekit.net/af/e69b71/00000000000000003b9b0ee6/27/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3") format("woff"),url("https://use.typekit.net/af/e69b71/00000000000000003b9b0ee6/27/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n5&v=3") format("opentype");
font-display:swap;font-style:normal;font-weight:500;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/5b0ee2/00000000000000003b9b0ee7/27/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i6&v=3") format("woff2"),url("https://use.typekit.net/af/5b0ee2/00000000000000003b9b0ee7/27/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i6&v=3") format("woff"),url("https://use.typekit.net/af/5b0ee2/00000000000000003b9b0ee7/27/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i6&v=3") format("opentype");
font-display:swap;font-style:italic;font-weight:600;
}

@font-face {
font-family:"urw-din";
src:url("https://use.typekit.net/af/084e58/00000000000000003b9b0ee8/27/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n6&v=3") format("woff2"),url("https://use.typekit.net/af/084e58/00000000000000003b9b0ee8/27/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n6&v=3") format("woff"),url("https://use.typekit.net/af/084e58/00000000000000003b9b0ee8/27/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n6&v=3") format("opentype");
font-display:swap;font-style:normal;font-weight:600;
}

@font-face {
font-family:"urw-din-condensed";
src:url("https://use.typekit.net/af/d3af1d/00000000000000003b9b0ef9/27/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("woff2"),url("https://use.typekit.net/af/d3af1d/00000000000000003b9b0ef9/27/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("woff"),url("https://use.typekit.net/af/d3af1d/00000000000000003b9b0ef9/27/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3") format("opentype");
font-display:swap;font-style:normal;font-weight:700;
}

@font-face {
font-family:"urw-din-condensed";
src:url("https://use.typekit.net/af/a2b1ca/00000000000000003b9b0efa/27/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("woff2"),url("https://use.typekit.net/af/a2b1ca/00000000000000003b9b0efa/27/d?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("woff"),url("https://use.typekit.net/af/a2b1ca/00000000000000003b9b0efa/27/a?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=i7&v=3") format("opentype");
font-display:swap;font-style:italic;font-weight:700;
}
`;
